import { createContext, useContext, useEffect, useState } from "react";
import { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/components/ui/use-toast";

type AuthContextType = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  signUp: (email: string, password: string, metadata?: any) => Promise<void>;
  signIn: (email: string, password: string) => Promise<void>;
  signInWithSocial: (provider: "google" | "github") => Promise<void>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const initializeAuth = async () => {
      try {
        const { data: { session: currentSession }, error } = await supabase.auth.getSession();
        
        if (error) {
          console.error("Error getting session:", error);
          if (mounted) {
            setSession(null);
            setUser(null);
            setLoading(false);
          }
          return;
        }

        if (mounted) {
          setSession(currentSession);
          setUser(currentSession?.user ?? null);
          setLoading(false);
        }
      } catch (error) {
        console.error("Error initializing auth:", error);
        if (mounted) {
          setSession(null);
          setUser(null);
          setLoading(false);
        }
      }
    };

    initializeAuth();

    // Set up the auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, currentSession) => {
        if (!mounted) return;

        console.log("Auth state changed:", event, currentSession?.user?.id);

        switch (event) {
          case 'SIGNED_IN':
            if (currentSession) {
              setSession(currentSession);
              setUser(currentSession.user);
              setLoading(false);
              // Only show welcome toast if this is a fresh sign-in
              if (localStorage.getItem('justSignedIn') === 'true') {
                toast({
                  title: "Signed in successfully",
                  description: "Welcome back!",
                });
                localStorage.removeItem('justSignedIn');
              }
            }
            break;

          case 'SIGNED_OUT':
            setSession(null);
            setUser(null);
            setLoading(false);
            toast({
              title: "Signed out successfully",
              description: "You have been signed out.",
            });
            break;

          case 'USER_UPDATED':
            if (currentSession) {
              setSession(currentSession);
              setUser(currentSession.user);
              setLoading(false);
            }
            break;

          case 'TOKEN_REFRESHED':
            if (currentSession) {
              setSession(currentSession);
              setUser(currentSession.user);
              setLoading(false);
            }
            break;

          default:
            if (currentSession) {
              setSession(currentSession);
              setUser(currentSession.user);
            }
            setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, metadata?: any) => {
    try {
      // Attempt to sign up
      const { error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            ...metadata,
            provider: 'email',
            providers: ['email']
          },
        },
      });

      if (error) {
        // If it's a "User already registered" error, check if it's a social account
        if (error.message === 'User already registered') {
          // Try to sign in with the email to check the provider
          const { data: signInData, error: signInError } = await supabase.auth.signInWithPassword({
            email,
            password: 'dummy-password-for-check'
          });

          // If sign in fails with invalid credentials, it's likely a social account
          if (signInError?.message === 'Invalid login credentials') {
            throw new Error(`This email appears to be associated with a social account. Please try signing in with Google or GitHub instead.`);
          }
        }
        throw error;
      }

      toast({
        title: "Account created",
        description: "Please check your email for verification.",
      });
    } catch (error: any) {
      toast({
        title: "Error signing up",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // Set flag for fresh sign-in before attempting sign in
      localStorage.setItem('justSignedIn', 'true');

      const { error } = await supabase.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        // Remove flag if sign-in failed
        localStorage.removeItem('justSignedIn');
        throw error;
      }
    } catch (error: any) {
      // Remove flag if there was an error
      localStorage.removeItem('justSignedIn');
      toast({
        title: "Error signing in",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signInWithSocial = async (provider: "google" | "github") => {
    try {
      // Set flag for fresh sign-in
      localStorage.setItem('justSignedIn', 'true');
      
      const { data, error } = await supabase.auth.signInWithOAuth({
        provider,
        options: {
          redirectTo: `${window.location.origin}/auth/callback`,
          queryParams: {
            access_type: 'offline',
            prompt: 'consent',
          }
        }
      });

      if (error) throw error;

    } catch (error: any) {
      // Remove flag if there was an error
      localStorage.removeItem('justSignedIn');
      toast({
        title: "Error signing in",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  const signOut = async () => {
    try {
      // Clear any stored session data first
      localStorage.removeItem('supabase.auth.token');
      localStorage.removeItem('justSignedIn');
      
      // Sign out from Supabase
      const { error } = await supabase.auth.signOut();
      if (error) throw error;

      // Force clear the session state
      setSession(null);
      setUser(null);
      
      // Clear any stored session data after signout
      localStorage.clear();
    } catch (error: any) {
      toast({
        title: "Error signing out",
        description: error.message,
        variant: "destructive",
      });
      throw error;
    }
  };

  return (
    <AuthContext.Provider
      value={{
        session,
        user,
        loading,
        signUp,
        signIn,
        signInWithSocial,
        signOut,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
}
